import { message as AntdMessage } from "antd";
import {
  action,
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { createContext } from "react";
import * as _ from "lodash";
import {
  addPurchaseLink,
  checkGameMomentRequest,
  checkLastNameRequest,
  fetchGameMomentsRequest,
  getCroppedVideo,
  getPurchasedImage,
  reservePrintOrderNumber,
} from "../axios/routes/moment";
import {
  CanvasPositionData,
  EditorSaveType,
  SelectedMomentosData,
} from "../interfaces/moment";
import {
  Coords,
  GetGameMomentsRequests,
  IntervalGamePopulated,
  MomentResponse,
  MomentSeat,
  UniqueUser,
} from "../shared/interfaces";
import { getGameStartDate } from "../utils/date";
import { getViewAreaPosition } from "../utils/image";
import { showErrorNotification } from "../utils/notification";
import { DEFAULT_SIZE, MAX_SPECIAL_SECTION_WIDTH, StorageServiceContext, StorageServiceInstance } from "./storage";
import { registerClearStaleCameraForSeat } from "./moment-seat-clear-hook";
import { GetCroppedVideoRequest } from "../shared/interfaces/moment";
import { MomentImageCacheService } from "./image-cache-service/moment-image-cache-service";
import { addPendingPayment, getDownloadPaidStatus, payForTheGame, updateLastSessionDate } from "../axios/routes/user";
import { AppServiceInstance } from "./app";
import { Routes } from "../utils/routes";
import { prepareSectionRowSeat, resolveSuiteMappedPlace } from "../utils/suite";
import {
  ASPECT_RATIO_LIST, ImageCoordinates, ImageCropData, ImageSize,
  calculateImageSize, calculateImageStartPosition, DownloadData,
} from "../utils/sharedFunctions";

// additional service information for the loaded moment
export interface MomentData {
  // referenced moment id
  _id: string;
  // currently selected moment slide
  selectedMomentoSlide: number;
  // moment GIF is not prepared
  gifLoading?: boolean;
  // moment GIF Image
  gifImageToPlay?: any;
}

export const MOMENTS_PAGE_SIZE = 10;

class MomentService {
  cacheService: MomentImageCacheService = new MomentImageCacheService();

  @observable momentList: Array<MomentResponse> = [];

  // ability to prepare not all momentos at once
  @observable momentsPreparedUntil = 0;

  // to know unpaid moments count
  @observable totalMomentsCount = 0;

  @observable totalMomentosCount = 0;

  // to know if moments are paid
  @observable isPaid = false;

  // to know if download is paid
  @observable isDownloadPaid = false;

  // additional information about moments
  @observable momentsData: Array<MomentData> = [];

  @observable selectedMoment: MomentResponse = null;

  @observable loading = false;

  @observable lastMomentRequest: GetGameMomentsRequests = null;

  private defaultZoomWidth: number = null;

  private defaultZoomHeight: number = null;

  private centerCoords: Coords = null;

  private offsetY: number = null;

  private horizontalOffset: number = null;

  private verticalOffset: number = null;

  private timeOut = 1;

  private returnedMomentWidth = 0;

  private returnedMomentHeight = 0;

  @observable editorAspect: ASPECT_RATIO_LIST = ASPECT_RATIO_LIST["3_4"];

  @observable selectedMomentoSlide: number = null;

  // for old momento page
  @observable gifPlaying = false;

  // for old momento page
  @observable gifLoading = false;

  // for old momento page
  @observable gifImageToPlay: string = null;

  // moment to scroll to after navigation
  @observable momentIdToScroll: string = null;

  // show moments from this one
  @observable startingMomentIndex = 0;

  private momentImagesRefs: Array<any> = [];

  @observable DEBUG_APP_TYPE = ""; // "" or "pregenGIF"

  @observable specialSectionImageInfo: MomentResponse[] = [];

  /** Total published momento images for the current special/GA seat (Place screen CTA). */
  @observable specialSectionPublishedPhotosCount = 0;

  @observable specialSectionImageLoading = false;

  @observable specialSecitonImageCompressRate = 1.0;

  /**
   * GA place-picker: Vision section frame from the API request plus delivered bitmap size.
   * Coordinates are stored in sectionWidth×sectionHeight space, then mapped to the bitmap.
   */
  @observable placePickerSectionFrame: {
    sectionWidth: number;
    sectionHeight: number;
    bitmapWidth?: number;
    bitmapHeight?: number;
  } | null = null;

  @observable shareDialogIsVisible = false;

  @observable shareType: string = null;

  @observable shareFiles: any = null;

  @action hideShareDialog = () => {
    this.shareDialogIsVisible = false;
    this.shareType = null;
    this.shareFiles = null;
  };

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => StorageServiceInstance.selectedMoment,
      (id) => {
        this.selectedMoment = this.momentList.find((m) => m._id === id);
      },
    );

    reaction(
      () => StorageServiceInstance.selectedMomento,
      (id) => {
        this.selectedMomentoSlide = id;
      },
    );

    this.cacheService.gifImages$.subscribe((caches) => {
      if (!this.isPregenerateGIFApp() || !caches?.length) {
        return;
      }
      runInAction(() => {
        caches.forEach((cache) => {
          const hasGif = !!cache.gifImage?.src;
          this.momentsData = this.momentsData.map((mData) => (
            mData._id === cache.momentId
              ? {
                ...mData,
                gifLoading: !hasGif,
                gifImageToPlay: cache.gifImage?.src || null,
              }
              : mData
          ));
        });
      });
    });

    registerClearStaleCameraForSeat(() => {
      this.clearStaleCameraForSeatChange();
    });
  }

  public getSpecialSectionSlicedArea = () => {
    const specialSection = StorageServiceInstance.getSelectedSpecialSection();
    if (!specialSection) {
      return {
        x: 0,
        y: 0,
        width: DEFAULT_SIZE,
        height: DEFAULT_SIZE,
        offsetX: 0,
        offsetY: 0,
      };
    }

    const sectionX = specialSection?.sectionX || 0;
    const sectionY = specialSection?.sectionY || 0;
    const sectionWidth = specialSection?.sectionWidth || DEFAULT_SIZE;
    const sectionHeight = specialSection?.sectionHeight || DEFAULT_SIZE;

    const slicingWidth = specialSection?.slicingWidth || DEFAULT_SIZE;
    const slicingHeight = specialSection?.slicingHeight || DEFAULT_SIZE;
    const center = StorageServiceInstance.getGaPlaceCenterInSectionSpace();
    let centerX: number;
    let centerY: number;
    if (center) {
      centerX = sectionX + center.centerX;
      centerY = sectionY + center.centerY;
    } else if (
      StorageServiceInstance.selectedPlaceX != null
      && StorageServiceInstance.selectedPlaceY != null
      && StorageServiceInstance.selectedPlaceWidth != null
      && StorageServiceInstance.selectedPlaceHeight != null
    ) {
      centerX = sectionX
        + StorageServiceInstance.selectedPlaceX + StorageServiceInstance.selectedPlaceWidth / 2.0;
      centerY = sectionY
        + StorageServiceInstance.selectedPlaceY + StorageServiceInstance.selectedPlaceHeight / 2.0;
    } else {
      centerX = sectionX + sectionWidth / 2.0;
      centerY = sectionY + sectionHeight / 2.0;
    }

    const x = centerX - slicingWidth / 2.0;
    const y = centerY - slicingHeight / 2.0;
    const offsetX = x < 0 ? -x : 0;
    const offsetY = y < 0 ? -y : 0;

    return {
      x,
      y,
      width: slicingWidth,
      height: slicingHeight,
      offsetX,
      offsetY,
    };
  }

  public getRectInSlicedArea = () => {
    const specialSection = StorageServiceInstance.getSelectedSpecialSection();
    if (!specialSection) {
      return {
        x: 0,
        y: 0,
        width: DEFAULT_SIZE,
        height: DEFAULT_SIZE,
        offsetX: 0,
        offsetY: 0,
      };
    }

    const sectionX = specialSection?.sectionX || 0;
    const sectionY = specialSection?.sectionY || 0;
    const sectionWidth = specialSection?.sectionWidth || DEFAULT_SIZE;
    const sectionHeight = specialSection?.sectionHeight || DEFAULT_SIZE;

    const slicingWidth = specialSection?.slicingWidth || DEFAULT_SIZE;
    const slicingHeight = specialSection?.slicingHeight || DEFAULT_SIZE;

    let areaX = 0;
    let areaWidth = slicingWidth;
    if (StorageServiceInstance.selectedPlaceWidth < slicingWidth) {
      areaWidth = StorageServiceInstance.selectedPlaceWidth;
      areaX = (slicingWidth - StorageServiceInstance.selectedPlaceWidth) / 2.0;
    }

    let areaY = 0;
    let areaHeight = slicingHeight;
    if (StorageServiceInstance.selectedPlaceHeight < slicingHeight) {
      areaHeight = StorageServiceInstance.selectedPlaceHeight;
      areaY = (slicingHeight - StorageServiceInstance.selectedPlaceHeight) / 2.0;
    }

    return {
      x: areaX,
      y: areaY,
      width: areaWidth,
      height: areaHeight,
    };
  }

  /** Avoid showing the previous seat's moments while a new context is loading. */
  private static mappedSeatContextMatches(a: MomentSeat, b: MomentSeat): boolean {
    if (!!a?.isSpecialSection !== !!b?.isSpecialSection) return false;
    if (a?.sectionName !== b?.sectionName) return false;
    if (a?.isSpecialSection) {
      return (
        a?.seatX === b?.seatX
        && a?.seatY === b?.seatY
        && a?.width === b?.width
        && a?.height === b?.height
      );
    }
    return a?.row === b?.row && a?.seat === b?.seat;
  }

  private generateMomentRequest = (
    useSlicingSizesForSpecialSection: boolean,
    compressSpecialSections: boolean,
    section?: string,
    row?: string,
    seat?: string,
    fromMoment?: number,
    toMoment?: number,
  ): GetGameMomentsRequests => {
    if (StorageServiceInstance.isGaMomentContext()) {
      const specialSection = StorageServiceInstance.getSelectedSpecialSection();

      let seatX = specialSection?.sectionX || 0;
      let seatY = specialSection?.sectionY || 0;
      let seatWidth = specialSection?.sectionWidth || DEFAULT_SIZE;
      let seatHeight = specialSection?.sectionHeight || DEFAULT_SIZE;

      // area with slicing sizes accross the center of place area
      if (useSlicingSizesForSpecialSection) {
        const persistedSlice = StorageServiceInstance.getGaPersistedSlice();
        if (persistedSlice) {
          seatX = persistedSlice.x;
          seatY = persistedSlice.y;
          seatWidth = persistedSlice.width;
          seatHeight = persistedSlice.height;
        } else {
          const slicedArea = this.getSpecialSectionSlicedArea();
          seatX = slicedArea.x;
          seatY = slicedArea.y;
          seatWidth = slicedArea.width;
          seatHeight = slicedArea.height;
        }
      }

      const maxSize = seatWidth > seatHeight ? seatWidth : seatHeight;
      const resizeWidth = compressSpecialSections && maxSize > MAX_SPECIAL_SECTION_WIDTH ? MAX_SPECIAL_SECTION_WIDTH : undefined;
      const compressRate = !!resizeWidth && maxSize > 0 ? resizeWidth / maxSize : undefined;

      return {
        mappedSeat: {
          sectionName: section || StorageServiceInstance.selectedSpecialSection,
          row: "",
          seat: "",
          isSpecialSection: true,
          seatX,
          seatY,
          width: seatWidth,
          height: seatHeight,
        },
        from: StorageServiceInstance.selectedGameData.startDate,
        to: StorageServiceInstance.selectedGameData.finishDate || null,
        stadium: StorageServiceInstance.selectedGameData.stadiumId,
        resizeWidth,
        compressRate,
        gameId: StorageServiceInstance.selectedGameData._id,
        email: StorageServiceInstance.selectedEmail,
        ...this.momentPagingFields(fromMoment, toMoment),
      };
    }

    let data: { section: string; row: string; seat: string };
    if (section && row && seat) {
      data = { section, row, seat };
    } else if (StorageServiceInstance.showSuites && StorageServiceInstance.selectedSuite) {
      data = resolveSuiteMappedPlace(
        StorageServiceInstance.selectedSuite,
        StorageServiceInstance.allSuites,
      );
    } else {
      data = prepareSectionRowSeat(
        section || StorageServiceInstance.selectedSection,
        row || StorageServiceInstance.selectedRow,
        seat || StorageServiceInstance.selectedSeat,
        "",
        false,
      );
    }

    return {
      mappedSeat: {
        sectionName: data.section,
        row: data.row,
        seat: data.seat,
      },
      from: StorageServiceInstance.selectedGameData.startDate,
      to: StorageServiceInstance.selectedGameData.finishDate || null,
      stadium: StorageServiceInstance.selectedGameData.stadiumId,
      gameId: StorageServiceInstance.selectedGameData._id,
      email: StorageServiceInstance.selectedEmail,
      ...this.momentPagingFields(fromMoment, toMoment),
    };
  };

  /** Omit fromMoment/toMoment so POST /api/moment returns every published moment (not a page of 10). */
  private momentPagingFields = (
    fromMoment?: number,
    toMoment?: number,
  ): Pick<GetGameMomentsRequests, "fromMoment" | "toMoment"> => {
    const paging: Pick<GetGameMomentsRequests, "fromMoment" | "toMoment"> = {};
    if (typeof fromMoment === "number" && Number.isFinite(fromMoment)) {
      paging.fromMoment = fromMoment;
    }
    if (typeof toMoment === "number" && Number.isFinite(toMoment)) {
      paging.toMoment = toMoment;
    }
    return paging;
  };

  public checkMoments = async (
    section: string,
    row: string,
    seat: string,
    suite?: string,
    show?: boolean,
  ): Promise<boolean> => {
    const data = (suite && show)
      ? resolveSuiteMappedPlace(suite, StorageServiceInstance.allSuites)
      : prepareSectionRowSeat(section, row, seat, suite, show);
    const request = this.generateMomentRequest(false, false, data.section, data.row, data.seat);

    const { message, success } = await checkGameMomentRequest(request);

    if (message) {
      AntdMessage.warning(message);
    }

    return success;
  };

  public checkLastName = async (
    gameId: string,
    lastName: string,
    place?: { section?: string; row?: string; seat?: string },
    opts?: { silent?: boolean; unblurOptOut?: boolean },
  ): Promise<boolean> => {
    const { message, success } = await checkLastNameRequest({
      gameId,
      lastName,
      section: place?.section,
      row: place?.row,
      seat: place?.seat,
      unblurOptOut: opts?.unblurOptOut === true,
    });

    if (message && !opts?.silent) {
      AntdMessage.warning(message);
    }

    return success;
  };

  private static hasReservedSeatForMomentsPrefetch(): boolean {
    return StorageServiceInstance.hasReservedSeatForGameMoments();
  }

  /**
   * Start full moments fetch + client precache as soon as reserved seat and game are saved.
   * Skips GA/special (seat page uses `fetchSpecialSectionImageData` for that path). No-op if
   * already loading, or list already matches this seat and email (e.g. prefetch completed).
   */
  public prefetchMomentListForCurrentSeat = (): void => {
    if (!StorageServiceInstance.selectedGameData) return;
    if (StorageServiceInstance.isGaMomentContext()) return;
    if (!MomentService.hasReservedSeatForMomentsPrefetch()) return;
    if (this.loading) return;

    const nextReq = this.generateMomentRequest(true, false);
    if (this.momentList.length > 0 && this.lastMomentRequest) {
      const seatMatches = MomentService.mappedSeatContextMatches(
        this.lastMomentRequest.mappedSeat,
        nextReq.mappedSeat,
      );
      const prevEmail = String(this.lastMomentRequest.email || "").trim().toLowerCase();
      const nextEmail = String(nextReq.email || "").trim().toLowerCase();
      if (seatMatches && prevEmail === nextEmail) {
        return;
      }
    }

    this.fetchMomentList().catch((err) => {
      console.error("[prefetchMomentListForCurrentSeat]", err);
    });
  };

  public fetchMomentListWithTimeout = async () => {
    // moments are already loaded or loading is active
    if (this.momentList.length || this.loading) {
      return;
    }

    // try again with progressing timeout to load selected game
    if (!StorageServiceInstance.selectedGameData) {
      if (this.timeOut > 10) {
        this.timeOut = 1;
        // stop attempts
        return;
      }

      const currentTimeOut = this.timeOut * 1000;
      if (this.timeOut === 10) {
        this.timeOut = 15;
      }

      if (this.timeOut === 5) {
        this.timeOut = 10;
      }

      if (this.timeOut === 1) {
        this.timeOut = 5;
      }

      setTimeout(() => {
        this.fetchMomentListWithTimeout();
      }, currentTimeOut);

      return;
    }

    if (!StorageServiceInstance.isMomentFetchSeatContextReady()) {
      return;
    }

    this.fetchMomentList();
  }

  // return moments of the game first
  private compareMoments = (m1, m2) => {
    if (m2.isMOTG && !m1.isMOTG) {
      return 1;
    }

    if (!m2.isMOTG && m1.isMOTG) {
      return -1;
    }

    return m2.momentStart - m1.momentStart;
  }

  /** Refetch GA fan photos when restored slice/center differs from the last API request. */
  public refetchGaMomentsIfSeatChanged = async (): Promise<void> => {
    if (!StorageServiceInstance.isGaMomentContext() || !StorageServiceInstance.selectedGameData) {
      return;
    }
    const request = this.generateMomentRequest(true, false);
    const matches = this.lastMomentRequest != null
      && MomentService.mappedSeatContextMatches(
        this.lastMomentRequest.mappedSeat,
        request.mappedSeat,
      );
    if (matches && this.momentList.length > 0) {
      return;
    }
    await this.fetchMomentList();
  };

  public fetchMomentList = async (prepareUntil = 0) => {
    if (this.loading || !StorageServiceInstance.selectedGameData) return;
    if (!StorageServiceInstance.isMomentFetchSeatContextReady()) {
      return;
    }
    this.timeOut = 1;
    const pageSize = prepareUntil > 0 ? prepareUntil : 0;

    // VCB MMNT-161
    // if (!this.dependenciesLoaded()) {
    //   this.schedule(this.fetchMomentList);
    //   return;
    // }

    // Do not clear momentList/momentsData before the request completes. Clearing caused
    // observers (e.g. my_moments) to see an empty list and re-trigger fetch, and left the
    // UI blank while refreshing — especially noticeable after heavy work (bulk ZIP) then back.
    this.loading = true;
    try {
      const request: GetGameMomentsRequests = pageSize > 0
        ? this.generateMomentRequest(true, false, undefined, undefined, undefined, 0, pageSize)
        : this.generateMomentRequest(true, false);

      const seatContextChanged = this.lastMomentRequest == null
        || !MomentService.mappedSeatContextMatches(
          this.lastMomentRequest.mappedSeat,
          request.mappedSeat,
        );
      if (seatContextChanged) {
        runInAction(() => {
          // Avoid using the previous seat's mappedSeat for GA slice scaling / layout.
          this.lastMomentRequest = null;
          this.momentList = [];
          this.momentsData = [];
          this.momentsPreparedUntil = 0;
          this.totalMomentsCount = 0;
          this.totalMomentosCount = 0;
          // Fan canvas scales reserved crops from API frame → natural; only clear on seat change.
          this.returnedMomentWidth = 0;
          this.returnedMomentHeight = 0;
          // Drop cached images from the prior seat context (IDs/URLs can overlap inventory indices).
          this.cacheService.resetForSeatContextChange();
        });
      }

      const { data, error } = await fetchGameMomentsRequest(request);

      this.momentIdToScroll = null;
      this.startingMomentIndex = 0;
      if (error) {
        this.momentList = [];
        this.momentsData = [];
        this.totalMomentsCount = 0;
        this.totalMomentosCount = 0;
        this.isPaid = true;
        this.momentsPreparedUntil = 0;
        AntdMessage.warning(error);
      } else if (!data || !Array.isArray(data.moments)) {
        this.momentList = [];
        this.momentsData = [];
        this.totalMomentsCount = 0;
        this.totalMomentosCount = 0;
        this.isPaid = true;
        this.momentsPreparedUntil = 0;
        const fallback = (data as { error?: string; message?: string } | null)?.error
          || (data as { error?: string; message?: string } | null)?.message;
        AntdMessage.warning(
          typeof fallback === "string" && fallback.trim() ? fallback.trim() : "Could not load moments for this seat.",
        );
      } else {
        const sortedMoments = data.moments.sort(this.compareMoments);
        runInAction(() => {
          this.lastMomentRequest = request;
          this.momentsData = sortedMoments.map((m) => {
            return {
              _id: m._id,
              selectedMomentoSlide: 0,
              gifLoading: true,
            };
          });

          this.momentList = sortedMoments;
          this.totalMomentsCount = data.momentsTotalCount || this.momentList.length;
          this.totalMomentosCount = data.momentosTotalCount
            || this.momentList.reduce((acc, oneMoment) => {
              const newAcc = acc + oneMoment.momentoLinks.length || 0;
              return newAcc;
            }, 0);
          this.isPaid = data.isPaid || false;
          this.isDownloadPaid = data.isDownloadPaid || false;
          this.returnedMomentWidth = data.width;
          this.returnedMomentHeight = data.height;
          this.momentsPreparedUntil = pageSize > 0
            ? pageSize
            : (data.momentsTotalCount || this.momentList.length);

          StorageServiceInstance.setMomentCameraData(data);

          if (
            StorageServiceInstance.isGaMomentContext()
            && request.mappedSeat?.isSpecialSection
          ) {
            StorageServiceInstance.setGaPersistedSlice({
              x: request.mappedSeat.seatX || 0,
              y: request.mappedSeat.seatY || 0,
              width: request.mappedSeat.width || DEFAULT_SIZE,
              height: request.mappedSeat.height || DEFAULT_SIZE,
            });
          }

          this.defaultZoomWidth = data.defaultZoomWidth;
          this.defaultZoomHeight = data.defaultZoomHeight;
          this.centerCoords = data.relativeCoords;
          this.offsetY = data.offsetTop;
          this.horizontalOffset = data.horizontalOffset;
          this.verticalOffset = data.verticalOffset;

          if (StorageServiceInstance.selectedMoment) {
            this.selectedMoment = this.momentList.find(
              (m) => m._id === StorageServiceInstance.selectedMoment,
            );
            // VCB MMNT-161
            // Let's remove this because I don't think it's necessary...
            // this.setAspectRatioWithStore();
          }
        });

        this.cacheService.generateGIFs = this.isPregenerateGIFApp();
        const pregenUntil = this.cacheService.generateGIFs
          ? Math.min(MOMENTS_PAGE_SIZE, this.momentList.length)
          : 1;
        try {
          await this.cacheService.preCacheMomentImages(this.momentList, 0, pregenUntil);
        } catch {
          // Image preload is best-effort; do not fail the moment list when presign/CDN errors.
        }
      }
    } catch (err) {
      showErrorNotification(`[fetchMomentList] ${err.message}`);
    } finally {
      this.loading = false;
    }
  };

  public precacheMoments = (from = 0, count = 1): void => {
    if (!this.momentList) {
      return;
    }
    if (from >= this.momentList.length) {
      return;
    }

    const to = Math.min(from + count, this.momentList.length);
    this.cacheService.preCacheMomentImages(this.momentList, from, to).catch(() => {
      // Preload is best-effort; failed URLs (expired presigns, etc.) must not surface as
      // unhandled rejections — the fan UI loads images on demand.
    });
  }

  public fetchMomentListPart = async (from = 0, to = 0) => {
    if (this.loading || !StorageServiceInstance.selectedGameData) return;
    if (!StorageServiceInstance.isMomentFetchSeatContextReady()) {
      return;
    }
    const loadUntil = to || from + MOMENTS_PAGE_SIZE;
    if (loadUntil <= this.momentsPreparedUntil) {
      return;
    }

    this.loading = true;
    try {
      const request: GetGameMomentsRequests = this.generateMomentRequest(true, false, undefined, undefined, undefined, from, loadUntil);
      const { data, error } = await fetchGameMomentsRequest(request);

      if (error) {
        AntdMessage.warning(error);
      } else if (data && Array.isArray(data.moments)) {
        runInAction(() => {
          const sortedPage = data.moments.sort(this.compareMoments);
          const seen = new Set(this.momentList.map((m) => String(m._id)));
          const appended = sortedPage.filter((m) => !seen.has(String(m._id)));
          if (appended.length) {
            this.momentList = [...this.momentList, ...appended];
            this.momentsData = [
              ...this.momentsData,
              ...appended.map((m) => ({
                _id: m._id,
                selectedMomentoSlide: 0,
                gifLoading: true,
              })),
            ];
          }
          this.totalMomentsCount = data.momentsTotalCount || this.momentList.length;
          this.totalMomentosCount = data.momentosTotalCount
            || this.momentList.reduce((acc, oneMoment) => {
              const newAcc = acc + (oneMoment.momentoLinks?.length || 0);
              return newAcc;
            }, 0);
          this.momentsPreparedUntil = loadUntil;
        });
        try {
          await this.cacheService.preCacheMomentImages(this.momentList, from, loadUntil);
        } catch {
          // best-effort preload
        }
      }
    } catch (err) {
      showErrorNotification(`[fetchMomentList] ${err.message}`);
    } finally {
      this.loading = false;
    }
  };

  @action public setSelectedMomentByMomentId = (momentId: string) => {
    const found = this.momentList.find((m) => m._id === momentId);
    if (found) {
      this.selectedMoment = found;
    }
  };

  @action public selectMomentoSlide = (slide: number) => {
    this.selectedMomentoSlide = slide;
    if (this.selectedMoment) {
      this.setAspectRatioWithStore();
    }
  };

  /**
   * Sets editor's aspect ratio to the saved aspect ratio
   * it will search saved aspect as following for: current momento -> current moment -> global
   * If nothing found in store sets default aspect ratio
   * @param slide number of slide or 0 if none selected
   */
  @action setAspectRatioWithStore = () => {
    const savedCropData = StorageServiceInstance.getImageCropData(
      this.selectedMoment._id,
      this.selectedMomentoSlide || 0,
    );
    const newAspect = savedCropData?.aspect || ASPECT_RATIO_LIST["8_10"];
    console.log(`Set editor's aspect ratio to ${newAspect}`);
    this.editorAspect = newAspect;
  };

  @action public changeAspect = (aspect: ASPECT_RATIO_LIST): void => {
    this.editorAspect = aspect;
  };

  private calculateImageStartPosition = (
    cropData: ImageCropData | null,
  ): ImageCoordinates => {
    const c = this.centerCoords ?? StorageServiceInstance.centerCoords;
    const dw = this.defaultZoomWidth ?? StorageServiceInstance.defaultZoomWidth ?? 400;
    const dh = this.defaultZoomHeight ?? StorageServiceInstance.defaultZoomHeight ?? 500;
    const oy = this.offsetY ?? StorageServiceInstance.offsetY ?? 0;
    return calculateImageStartPosition(
      cropData,
      c?.x ?? 0,
      c?.y ?? 0,
      dw,
      dh,
      oy,
      this.horizontalOffset ?? 0,
    );
  };

  private calculateImageSize = (cropData: ImageCropData | null): ImageSize => {
    const dw = this.defaultZoomWidth ?? StorageServiceInstance.defaultZoomWidth ?? 400;
    const dh = this.defaultZoomHeight ?? StorageServiceInstance.defaultZoomHeight ?? 500;
    return calculateImageSize(cropData, dw, dh);
  };

  /**
   * Reserved-seat API camera defaults must not stay in memory when switching to GA (or another seat).
   * Otherwise slice→bitmap scaling and crops apply on top of the wrong center/zoom.
   */
  @action public clearStaleCameraForSeatChange = (): void => {
    this.lastMomentRequest = null;
    this.centerCoords = null;
    this.defaultZoomWidth = null;
    this.defaultZoomHeight = null;
    this.offsetY = null;
    this.horizontalOffset = null;
    this.verticalOffset = null;
    this.returnedMomentWidth = 0;
    this.returnedMomentHeight = 0;
  };

  /**
   * Returns the position and size of the image to be displayed on canvas
   * @param _canvasWidth width of the canvas element
   * @param _canvasHeight height of the canvas element
   * @param momentId id of the moment we are searching crop data for
   * @param momentoIndex index of the momentO we are searching crop data for
   */
  public getCanvasImagePosition = (
    _canvasWidth: number,
    _canvasHeight: number,
    momentId: string,
    momentoIndex: number,
  ): CanvasPositionData => {
    const {
      width: canvasWidth,
      height: canvasHeight,
      x: areaOffsetX,
      y: areaOffsetY,
    } = getViewAreaPosition(
      _canvasWidth,
      _canvasHeight,
      ASPECT_RATIO_LIST["8_10"],
    );

    const savedCropData = StorageServiceInstance.getImageCropData(
      momentId,
      momentoIndex,
    );

    const { x: startXPosition, y: startYPosition } = this.calculateImageStartPosition(savedCropData);

    const { width: imageWidth, height: imageHeight } = this.calculateImageSize(savedCropData);

    return this.getCanvasSquareImagePosition(
      {
        x: startXPosition || 0,
        y: startYPosition || 0,
        width: imageWidth,
        height: imageHeight,
      },
      areaOffsetX,
      areaOffsetY,
      canvasWidth,
    );
  };

  /**
   * Disable Adjust Photo: fans cannot pan, so the photo and cover must be the
   * centered fanview. GA uses the 8:10 pin crop inside the slice. Reserved seats
   * use the section default zoom around the seat center, not the slicing box.
   */
  @action public applyLockedFanViewCrop = (): void => {
    if (StorageServiceInstance.selectedGameData?.team1?.disableAdjustPhoto !== true) {
      return;
    }

    if (StorageServiceInstance.isGaMomentContext()) {
      StorageServiceInstance.ensureCenteredFanViewCrop(this.getSpecialSectionSlicedArea());
      return;
    }

    const center = this.centerCoords ?? StorageServiceInstance.centerCoords;
    const dw = this.defaultZoomWidth ?? StorageServiceInstance.defaultZoomWidth;
    const dh = this.defaultZoomHeight ?? StorageServiceInstance.defaultZoomHeight;
    if (!center || !dw || !dh) return;

    const oy = this.offsetY ?? StorageServiceInstance.offsetY ?? 0;
    const start = calculateImageStartPosition(
      null,
      center.x,
      center.y,
      dw,
      dh,
      oy,
      this.horizontalOffset ?? 0,
    );
    const size = calculateImageSize(null, dw, dh);
    const rect = {
      x: start.x,
      y: start.y,
      width: size.width,
      height: size.height,
    };
    if (StorageServiceInstance.lockedFanViewAlreadyApplied(rect)) return;

    StorageServiceInstance.saveImageCropData({
      ...rect,
      aspect: ASPECT_RATIO_LIST["8_10"],
    });
  };

  /**
   * Persist the current API-centered default crop (same rect the canvas would use with no save)
   * so navigation / remount reads stable data from worker storage — mirrors opening Crop and saving
   * without forcing the user through the editor.
   */
  @action public seedFanViewCropIfAbsent = (
    momentId: string,
    momentoIndex: number,
  ): void => {
    if (!momentId || typeof momentoIndex !== "number" || momentoIndex < 0) {
      return;
    }
    if (StorageServiceInstance.hasMomentoSpecificCropData(momentId, momentoIndex)) {
      return;
    }
    // Editor "Apply" saves a seat-global crop and clears per-momento keys; re-seeding here would
    // immediately recreate momento rows and mask the saved global rect + filters on My Moments.
    if (StorageServiceInstance.hasGlobalCropData()) {
      return;
    }
    const mergedCenter = this.centerCoords ?? StorageServiceInstance.centerCoords;
    if (!mergedCenter) {
      return;
    }
    const refW = 400;
    const refH = 500;
    const pos = this.getCanvasImagePosition(refW, refH, momentId, momentoIndex);
    if (!pos.width || !pos.height) {
      return;
    }
    StorageServiceInstance.saveImageCropData(
      {
        x: pos.x,
        y: pos.y,
        width: pos.width,
        height: pos.height,
        aspect: ASPECT_RATIO_LIST["8_10"],
        momentApiFrameCrop: true,
      },
      momentId,
      momentoIndex,
    );
  };

  /**
   * Special case of `getCanvasImagePosition` when canvas is a square
   * Method cuts off the larger side of the image to make it square
   * @param imageData image size and position
   * @param areaOffsetX view area offset
   * @param areaOffsetY view area offset
   * @param viewAreaSize view area size (width = height)
   */
  private getCanvasSquareImagePosition = (
    imageData: ImageCropData,
    areaOffsetX: number,
    areaOffsetY: number,
    viewAreaSize: number,
  ): CanvasPositionData => {
    const imageRatio = imageData.height / imageData.width;
    if (imageData.height > imageData.width) {
      const newCanvasWidth = viewAreaSize / imageRatio;
      return {
        x: imageData.x,
        y: imageData.y,
        width: imageData.width,
        height: imageData.height,
        canvasXOffset: areaOffsetX + (viewAreaSize - newCanvasWidth) / 2.0,
        canvasYOffset: areaOffsetY,
        canvasWidth: newCanvasWidth,
        canvasHeight: viewAreaSize,
      };
    }
    if (imageData.height < imageData.width) {
      const newCanvasHeight = viewAreaSize * imageRatio;
      return {
        x: imageData.x,
        y: imageData.y,
        width: imageData.width,
        height: imageData.height,
        canvasXOffset: areaOffsetX,
        canvasYOffset: areaOffsetY + (viewAreaSize - newCanvasHeight) / 2.0,
        canvasWidth: viewAreaSize,
        canvasHeight: newCanvasHeight,
      };
    }

    // cropped image is already a square
    return {
      ...imageData,
      canvasXOffset: areaOffsetX,
      canvasYOffset: areaOffsetY,
      canvasWidth: viewAreaSize,
      canvasHeight: viewAreaSize,
    };
  };

  /**
   * Slice size in stadium/native space for the current GA request, or config fallback when
   * `lastMomentRequest` is still stale (e.g. right after switching reserved → GA).
   */
  public getSpecialSectionSliceDimensionsForCrop = (): { width: number; height: number } | null => {
    const ms = this.lastMomentRequest?.mappedSeat;
    if (ms?.isSpecialSection && ms.width && ms.height) {
      return { width: ms.width, height: ms.height };
    }
    const special = StorageServiceInstance.getSelectedSpecialSection();
    const w = special?.slicingWidth;
    const h = special?.slicingHeight;
    if (w && h) {
      return { width: w, height: h };
    }
    return null;
  };

  /** Map stored special-section crop (slice coordinates) into actual loaded image pixels. */
  public scaleSpecialSectionCropRectToBitmap = (
    rect: Pick<ImageCropData, "x" | "y" | "width" | "height">,
    imageNaturalWidth: number,
    imageNaturalHeight: number,
  ): { x: number; y: number; width: number; height: number } => {
    if (
      !StorageServiceInstance.isGaMomentContext()
      || !imageNaturalWidth
      || !imageNaturalHeight
    ) {
      return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
    }
    const slice = this.getSpecialSectionSliceDimensionsForCrop();
    if (!slice?.width || !slice?.height) {
      return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
    }
    const scaleX = imageNaturalWidth / slice.width;
    const scaleY = imageNaturalHeight / slice.height;
    return {
      x: rect.x * scaleX,
      y: rect.y * scaleY,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    };
  };

  /** API `width`/`height` from getGameMoments — coordinate frame for reserved-seat fan defaults. */
  public getReturnedMomentCoordinateFrame = (): { width: number; height: number } => ({
    width: this.returnedMomentWidth || 0,
    height: this.returnedMomentHeight || 0,
  });

  /**
   * Map crop from moments API pixel frame to loaded image natural pixels (e.g. high-res URL).
   * Identity for GA/special; no-op when frame is unknown (0) or matches natural size.
   */
  public scaleReservedMomentCropToNatural = (
    rect: Pick<ImageCropData, "x" | "y" | "width" | "height">,
    naturalWidth: number,
    naturalHeight: number,
  ): Pick<ImageCropData, "x" | "y" | "width" | "height"> => {
    if (StorageServiceInstance.isGaMomentContext()) {
      return rect;
    }
    const fw = this.returnedMomentWidth;
    const fh = this.returnedMomentHeight;
    if (!naturalWidth || !naturalHeight || !fw || !fh) {
      return rect;
    }
    if (naturalWidth === fw && naturalHeight === fh) {
      return rect;
    }
    const scaleX = naturalWidth / fw;
    const scaleY = naturalHeight / fh;
    return {
      x: rect.x * scaleX,
      y: rect.y * scaleY,
      width: rect.width * scaleX,
      height: rect.height * scaleY,
    };
  };

  /** Cropper reports bitmap pixels; persist GA crops in slice space to match place + canvas. */
  public normalizeEditorCropForStorage = (
    cropData: ImageCropData,
    imageNaturalWidth: number,
    imageNaturalHeight: number,
  ): ImageCropData => {
    if (
      !StorageServiceInstance.isGaMomentContext()
      || !imageNaturalWidth
      || !imageNaturalHeight
    ) {
      return cropData;
    }
    const slice = this.getSpecialSectionSliceDimensionsForCrop();
    if (!slice?.width || !slice?.height) {
      return cropData;
    }
    const scaleX = imageNaturalWidth / slice.width;
    const scaleY = imageNaturalHeight / slice.height;
    if (scaleX < 1e-6 || scaleY < 1e-6) {
      return cropData;
    }
    if (Math.abs(scaleX - 1) < 0.001 && Math.abs(scaleY - 1) < 0.001) {
      return cropData;
    }
    return {
      ...cropData,
      x: cropData.x / scaleX,
      y: cropData.y / scaleY,
      width: cropData.width / scaleX,
      height: cropData.height / scaleY,
    };
  };

  /**
   * Fetches the specific coordinates and sizes of the image
   * Is used while displaying in editor and downloading images
   * @param momentId if provided, will search crop data for this moment,
   * otherwise for currently selected moment
   * @param momentoSlide if provided, will search crop data for this momento,
   * otherwise for momento with index 0
   */
  public getEditorImagePosition = (
    momentId?: string,
    momentoSlide?: number,
  ): ImageCropData => {
    const savedCropData = StorageServiceInstance.getImageCropData(
      this.getMomentId(momentId),
      this.getMomentoIndex(momentoSlide),
    );

    const mergedCenter = this.centerCoords ?? StorageServiceInstance.centerCoords;
    if (!mergedCenter) {
      if (
        savedCropData
        && savedCropData.width > 0
        && savedCropData.height > 0
        && Number.isFinite(savedCropData.x)
        && Number.isFinite(savedCropData.y)
      ) {
        return {
          x: savedCropData.x,
          y: savedCropData.y,
          width: savedCropData.width,
          height: savedCropData.height,
          aspect: savedCropData?.aspect,
          filter: savedCropData?.filter,
          customAdjustments: savedCropData?.customAdjustments,
        };
      }
      const dw = this.defaultZoomWidth ?? StorageServiceInstance.defaultZoomWidth ?? 400;
      const dh = this.defaultZoomHeight ?? StorageServiceInstance.defaultZoomHeight ?? 500;
      return {
        x: 0,
        y: 0,
        width: dw,
        height: dh,
        aspect: savedCropData?.aspect,
        filter: savedCropData?.filter,
        customAdjustments: savedCropData?.customAdjustments,
      };
    }

    const { x, y } = this.calculateImageStartPosition(savedCropData);

    const { width, height } = this.calculateImageSize(savedCropData);

    return {
      x,
      y,
      width,
      height,
      aspect: savedCropData?.aspect,
      filter: savedCropData?.filter,
      customAdjustments: savedCropData?.customAdjustments,
    };
  };

  /**
   * Saves crop data inside the worker storage depending on the provided type
   * @param cropData position, size and aspect ratio data
   * @param type type of saving that we perform
   * `momento` - saves crop data only for currently selected momentO
   * `moment` - saves crop data for currently selected moment
   * `global` - saves crop data as global for all moments and momento of current game and seat
   */
  public saveEditorChanges = (
    cropData: ImageCropData,
    type: EditorSaveType,
    imageNatural?: { width: number; height: number } | null,
  ): void => {
    const normalized = imageNatural?.width && imageNatural?.height
      ? this.normalizeEditorCropForStorage(cropData, imageNatural.width, imageNatural.height)
      : cropData;
    const toSave = !StorageServiceInstance.isGaMomentContext()
      ? { ...normalized, momentApiFrameCrop: false }
      : normalized;

    switch (type) {
      case "global":
        StorageServiceInstance.saveImageCropData(toSave);
        if (this.isPregenerateGIFApp()) {
          this.reRenderAllGIFs();
        }
        break;
      case "moment":
        StorageServiceInstance.saveImageCropData(
          toSave,
          this.selectedMoment._id,
        );

        if (this.isPregenerateGIFApp()) {
          this.reRenderSingleGIF(this.selectedMoment._id);
        }
        break;
      case "momento":
        StorageServiceInstance.saveImageCropData(
          toSave,
          this.selectedMoment._id,
          this.selectedMomentoSlide || 0,
        );
        if (this.isPregenerateGIFApp()) {
          this.reRenderSingleGIF(this.selectedMoment._id);
        }
        break;
      case "momentoAndGlobal":
        // Save full crop + filter to global so it applies to ALL images on My Moments
        StorageServiceInstance.saveImageCropData(toSave);
        if (this.isPregenerateGIFApp()) {
          this.reRenderAllGIFs();
        }
        break;
      default:
        showErrorNotification("Wrong saving options");
    }
  };

  /**
   * Deletes crop data inside the worker storage depending on the provided type
   * @param type type of deletion that we perform
   * `momento` - deletes crop data only for currently selected momentO
   * `moment` - deletes crop data for currently selected moment,
   *    changes with `momento` type won't be deleted
   * `global` - deletes all global crop data, crop data for `momento` or `moment` won't be deleted
   */
  public deleteEditorChanges = (type: EditorSaveType): void => {
    switch (type) {
      case "global":
        StorageServiceInstance.removeImageCropData();
        if (this.isPregenerateGIFApp()) {
          this.reRenderAllGIFs();
        }
        break;
      case "moment":
        StorageServiceInstance.removeImageCropData(this.selectedMoment._id);
        if (this.isPregenerateGIFApp()) {
          this.reRenderSingleGIF(this.selectedMoment._id);
        }
        break;
      case "momento":
        if (!this.selectedMomentoSlide && this.selectedMomentoSlide !== 0) {
          AntdMessage.error(
            "No momento selected. Try to get back and select a new one",
          );
          return;
        }
        StorageServiceInstance.removeImageCropData(
          this.selectedMoment._id,
          this.selectedMomentoSlide,
        );
        if (this.isPregenerateGIFApp()) {
          this.reRenderSingleGIF(this.selectedMoment._id);
        }
        break;
      case "momentoAndGlobal":
        StorageServiceInstance.removeGlobalCropData();
        if (this.isPregenerateGIFApp()) {
          this.reRenderAllGIFs();
        }
        break;
      default:
        showErrorNotification("Wrong delete options");
    }
  };

  /**
   * Returns sponsorImage for selected moment,
   */
  public getSponsorOverlayForDownload = (): string | undefined => {
    if (!this.selectedMoment?._id || !this.momentList?.length) {
      return undefined;
    }
    const momentId = this.selectedMoment._id;
    const targetMomentIndex = this.momentList.findIndex(
      (m) => m._id === momentId,
    );

    const targetMoment = this.momentList[targetMomentIndex];

    if (!targetMoment) {
      AntdMessage.error("Something went wrong while searching for moment");
      throw new Error();
    }

    if (targetMoment.sponsorOverlay && targetMoment.sponsorOverlayImage) {
      return targetMoment.sponsorOverlayImage;
    }

    return undefined;
  };

  /**
   * Returns all data needed for image downloading including cropped image position,
   * link to src image and name for the file
   * @param wholeMoment `true` if we need to load all momento or gif, otherwise `false`
   * @param gif `true` is we need to load gif, otherwise `false`
   */
  public getDownloadDataArray = (
    wholeMoment?: boolean,
    gif?: boolean,
  ): DownloadData[] => {
    if (!this.selectedMoment?._id || !this.momentList?.length) {
      return [];
    }
    const momentId = this.selectedMoment._id;
    const targetMomentIndex = this.momentList.findIndex(
      (m) => m._id === momentId,
    );

    const targetMoment = this.momentList[targetMomentIndex];

    if (!targetMoment) {
      AntdMessage.error("Something went wrong while searching for moment");
      throw new Error();
    }

    if (!wholeMoment) {
      const position = this.getEditorImagePosition();
      const momentoIndex = this.getMomentoIndex();

      return [
        {
          ...position,
          link: targetMoment.momentoLinks[momentoIndex],
          name: this.generateDownloadName(targetMomentIndex, momentoIndex),
        },
      ];
    }

    return targetMoment.momentoLinks.map((link, index) => {
      const position = this.getEditorImagePosition(targetMoment._id, index);
      return <DownloadData>{
        ...position,
        link,
        name: this.generateDownloadName(targetMomentIndex, gif ? null : index),
      };
    });
  };

  public getDownloadDataArrayForMomento = (
    momentoIndex: number,
  ): DownloadData[] => {
    if (!this.selectedMoment?._id || !this.momentList?.length) {
      return [];
    }
    const momentId = this.selectedMoment._id;
    const targetMomentIndex = this.momentList.findIndex(
      (m) => m._id === momentId,
    );

    const targetMoment = this.momentList[targetMomentIndex];

    if (!targetMoment) {
      AntdMessage.error("Something went wrong while searching for moment");
      throw new Error();
    }

    const index = momentoIndex < targetMoment.momentoLinks.length ? momentoIndex : 0;
    const position = this.getEditorImagePosition(targetMoment._id, index);

    return [
      {
        ...position,
        link: targetMoment.momentoLinks[index],
        name: this.generateDownloadName(targetMomentIndex, index),
      },
    ];
  }

  /**
   * Returns all data needed for image downloading including cropped image position,
   * link to src image and name for the file
   * @param selectedIndexes selected indexes
   */
  public getDownloadSelectedMomentosArray = (
    selectedIndexes: SelectedMomentosData,
  ): DownloadData[] => {
    if (!this.selectedMoment?._id || !this.momentList?.length) {
      return [];
    }
    const momentId = this.selectedMoment._id;
    const targetMomentIndex = this.momentList.findIndex(
      (m) => m._id === momentId,
    );

    const targetMoment = this.momentList[targetMomentIndex];

    if (!targetMoment) {
      AntdMessage.error("Something went wrong while searching for moment");
      throw new Error();
    }

    const result: DownloadData[] = [];
    for (
      let index = 0, len = targetMoment.momentoLinks.length;
      index < len;
      index += 1
    ) {
      const link = targetMoment.momentoLinks[index];
      if (selectedIndexes[index]) {
        const position = this.getEditorImagePosition(targetMoment._id, index);
        result.push({
          ...position,
          link,
          name: this.generateDownloadName(targetMomentIndex, index),
        });
      }
    }

    return result;
  };

  /**
   * Returns download data for a single moment (all images in that moment)
   */
  public getDownloadDataArrayForMoment = (momentId: string): DownloadData[] => {
    const momentIndex = this.momentList.findIndex((m) => m._id === momentId);
    const moment = this.momentList[momentIndex];
    if (!moment?.momentoLinks?.length) return [];

    const result: DownloadData[] = [];
    for (let momentoIndex = 0; momentoIndex < moment.momentoLinks.length; momentoIndex += 1) {
      const position = this.getEditorImagePosition(moment._id, momentoIndex);
      result.push({
        ...position,
        link: moment.momentoLinks[momentoIndex],
        name: this.generateDownloadName(momentIndex, momentoIndex),
      });
    }
    return result;
  };

  /**
   * Returns all download data for all moments in the game (all images from momentList)
   */
  public getDownloadDataArrayForGame = (): DownloadData[] => {
    const result: DownloadData[] = [];
    for (let momentIndex = 0; momentIndex < this.momentList.length; momentIndex += 1) {
      const moment = this.momentList[momentIndex];
      if (moment?.momentoLinks?.length) {
        for (let momentoIndex = 0; momentoIndex < moment.momentoLinks.length; momentoIndex += 1) {
          const position = this.getEditorImagePosition(moment._id, momentoIndex);
          result.push({
            ...position,
            link: moment.momentoLinks[momentoIndex],
            name: this.generateDownloadName(momentIndex, momentoIndex),
          });
        }
      }
    }
    return result;
  };

  /**
   * Generating file name that will be assigned to
   * the file during donwload
   * Sample name: "Lexington_Legends_vs_New_Team_06_15_2021_m0_0"
   * @param momentIndex index of the moment in moment list received from the server
   * @param momentoIndex index of the momento in the selected moment
   * or null if it shouldn't be specified in name, e.g. when gif is being donwloaded
   */
  private generateDownloadName = (
    momentIndex: number,
    momentoIndex: number = null,
  ): string => {
    const team1Name = StorageServiceInstance.selectedGameData.team1.name.replace(" ", "_");
    const team2Name = StorageServiceInstance.selectedGameData.team2.name.replace(" ", "_");

    const gameDate = getGameStartDate(
      StorageServiceInstance.selectedGameData.startDate,
    );

    const baseName = `${team1Name}_vs_${team2Name}_${gameDate}_m${momentIndex}`;

    return momentoIndex !== null ? `${baseName}_${momentoIndex}` : baseName;
  };

  private getMomentId = (momentId?: string): string => {
    return momentId || this.selectedMoment._id;
  };

  public getMomentoIndex = (momentoIndex?: number): number => {
    if (typeof momentoIndex === "number" && Number.isFinite(momentoIndex) && momentoIndex >= 0) {
      return momentoIndex;
    }
    if (
      typeof this.selectedMomentoSlide === "number"
      && Number.isFinite(this.selectedMomentoSlide)
      && this.selectedMomentoSlide >= 0
    ) {
      return this.selectedMomentoSlide;
    }
    return 0;
  };

  private dependenciesLoaded = (): boolean => {
    return (
      !!StorageServiceInstance.selectedGameData
      && !!(
        StorageServiceInstance.selectedSeat
        || (StorageServiceInstance.showSuites && StorageServiceInstance.selectedSuite)
      )
    );
  };

  private schedule = (func: () => void, time = 300): NodeJS.Timeout => {
    return setTimeout(() => {
      func();
    }, time);
  };

  public getCroppedVideo = async (
    request: GetCroppedVideoRequest,
  ): Promise<any> => {
    // if (!this.dependenciesLoaded()) {
    //   this.schedule(this.fetchMomentList);
    //   return;
    // }
    let result = null;
    try {
      result = await getCroppedVideo(request);
      console.log(
        `MomentService.getCroppedVideo() - received ${result.length} bytes.`,
      );
    } catch (err) {
      showErrorNotification(`[getCroppedVideo] ${err.message}`);
    }
    return result;
  };

  // old code, can be necessary in the future
  /**
   * Returns the position and size of the image to be displayed on canvas
   * Calculate offset of the image on the canvas
   * depending on the aspect ratio of canvas and cropped image
   * @param canvasWidth width of the canvas element
   * @param canvasHeight height of the canvas element
   * @param momentId id of the moment we are searching crop data for
   * @param momentoIndex index of the momentO we are searching crop data for
   */
  public getCanvasImagePositionCropImage_Old = (
    _canvasWidth: number,
    _canvasHeight: number,
    momentId: string,
    momentoIndex: number,
  ): CanvasPositionData => {
    const {
      width: canvasWidth,
      height: canvasHeight,
      x: areaOffsetX,
      y: areaOffsetY,
    } = getViewAreaPosition(
      _canvasWidth,
      _canvasHeight,
      ASPECT_RATIO_LIST["8_10"],
    );

    const canvasAspect = canvasWidth / canvasHeight;

    const savedCropData = StorageServiceInstance.getImageCropData(
      momentId,
      momentoIndex,
    );

    const imageAspect = savedCropData?.aspect || ASPECT_RATIO_LIST["8_10"];

    const { x: startXPosition, y: startYPosition } = this.calculateImageStartPosition(savedCropData);

    const { width: imageWidth, height: imageHeight } = this.calculateImageSize(savedCropData);

    // canvas is a square preview, need to decrease the bigger image side to fit area
    if (canvasAspect === 1) {
      return this.getCanvasSquareImagePositionCropImage_Old(
        {
          x: startXPosition,
          y: startYPosition,
          width: imageWidth,
          height: imageHeight,
        },
        areaOffsetX,
        areaOffsetY,
      );
    }

    // the height of the canvas is excesive, need to add blank spaces at the top and bottom
    if (canvasAspect > imageAspect) {
      // expected width with this aspect ratio and height
      const expectedWidth = canvasHeight * imageAspect;
      const extraCanvasWidth = canvasWidth - expectedWidth;
      const canvasXOffset = extraCanvasWidth / 2;

      return {
        x: startXPosition,
        y: startYPosition,
        width: imageWidth,
        height: imageHeight,
        canvasYOffset: areaOffsetY,
        canvasXOffset: canvasXOffset + areaOffsetX,
        canvasWidth: 0,
        canvasHeight: 0,
      };
    }

    // the width of the canvas is excesive, need to add blank spaces to left and right
    if (canvasAspect < imageAspect) {
      // expected height with this aspect ratio and width
      const expectedHeight = canvasWidth / imageAspect;
      const extraCanvasHeight = canvasHeight - expectedHeight;
      const canvasYOffset = extraCanvasHeight / 2;

      return {
        x: startXPosition,
        y: startYPosition,
        width: imageWidth,
        height: imageHeight,
        canvasYOffset: canvasYOffset + areaOffsetY,
        canvasXOffset: areaOffsetX,
        canvasWidth: 0,
        canvasHeight: 0,
      };
    }

    // canvas and image aspect ratio is perfect match
    return {
      x: startXPosition,
      y: startYPosition,
      width: imageWidth,
      height: imageHeight,
      canvasYOffset: areaOffsetY,
      canvasXOffset: areaOffsetX,
      canvasWidth: 0,
      canvasHeight: 0,
    };
  };

  // old code, can be necessary in the future
  /**
   * Special case of `getCanvasImagePosition` when canvas is a square
   * Method cuts off the larger side of the image to make it square
   * @param imageData image size and position
   * @param areaOffsetX view area offset
   * @param areaOffsetY view area offset
   */
  private getCanvasSquareImagePositionCropImage_Old = (
    imageData: ImageCropData,
    areaOffsetX: number,
    areaOffsetY: number,
  ): CanvasPositionData => {
    // image height is too big, need to cut chunks top and bottom
    if (imageData.height > imageData.width) {
      const extraHeight = imageData.height - imageData.width;
      return {
        x: imageData.x,
        y: imageData.y + extraHeight / 2,
        width: imageData.width,
        height: imageData.height - extraHeight,
        canvasXOffset: areaOffsetX,
        canvasYOffset: areaOffsetY,
        canvasWidth: 0,
        canvasHeight: 0,
      };
    }
    // image  width is too big, need to cut chunks from left and right
    if (imageData.height < imageData.width) {
      const extraWidth = imageData.width - imageData.height;
      return {
        x: imageData.x + extraWidth / 2,
        y: imageData.y,
        width: imageData.width - extraWidth,
        height: imageData.height,
        canvasXOffset: areaOffsetX,
        canvasYOffset: areaOffsetY,
        canvasWidth: 0,
        canvasHeight: 0,
      };
    }
    // cropped image is already a square
    return {
      ...imageData,
      canvasXOffset: areaOffsetX,
      canvasYOffset: areaOffsetY,
      canvasWidth: 0,
      canvasHeight: 0,
    };
  };

  @action public setGIFPlaying = (playing: boolean) => {
    this.gifPlaying = playing;
    this.gifImageToPlay = null;
  };

  @action public setGIFLoading = (loading: boolean) => {
    this.gifLoading = loading;
  };

  @action public setGIFImageToPlay = (image: string | null) => {
    this.gifImageToPlay = image;
  };

  @action public setSelectedMomentoForMoment = (
    momentId: string,
    selected: number,
  ) => {
    this.momentsData = this.momentsData.map((moment) => {
      return moment._id === momentId
        ? { ...moment, selectedMomentoSlide: selected }
        : moment;
    });
  };

  @action public setMomentGIFPlaying = (
    momentId: string,
    gifPlaying: boolean,
  ) => {
    this.momentsData = this.momentsData.map((moment) => {
      return moment._id === momentId
        ? { ...moment, gifPlaying, gifImageToPlay: null }
        : moment;
    });
  };

  @action public setMomentGIFLoading = (
    momentId: string,
    gifLoading: boolean,
  ) => {
    this.momentsData = this.momentsData.map((moment) => {
      return moment._id === momentId ? { ...moment, gifLoading } : moment;
    });
  };

  @action public setMomentGIFImageToPlay = (
    momentId: string,
    setGIFImageToPlay: string | null,
  ) => {
    this.momentsData = this.momentsData.map((moment) => {
      return moment._id === momentId
        ? { ...moment, setGIFImageToPlay }
        : moment;
    });
  };

  @action public setMomentIdToScroll = (id: string) => {
    this.momentIdToScroll = id;
  };

  @action public reRenderSingleGIF = (momentId: string) => {
    this.momentsData = this.momentsData.map((mData) => {
      return mData._id === momentId
        ? {
          ...mData,
          gifLoading: true,
          gifImageToPlay: null,
        }
        : mData;
    });

    this.cacheService.renderSingleGIF(momentId);
  };

  @action public reRenderAllGIFs = () => {
    this.momentsData = this.momentsData.map((mData) => {
      return {
        ...mData,
        gifLoading: true,
        gifImageToPlay: null,
      };
    });

    this.cacheService.renderAllGIFs();
  };

  public isPregenerateGIFApp = () => {
    return this.DEBUG_APP_TYPE === "pregenGIF";
  }

  public isPlayImagesAsGIFApp = () => {
    return this.DEBUG_APP_TYPE === "";
  }

  public setPregenerateGIFAppType = (setMode = true) => {
    this.DEBUG_APP_TYPE = setMode ? "pregenGIF" : "";
  }

  @action public setStartingMomentIndex = async (index) => {
    if (this.momentList?.length > 0) {
      await this.fetchMomentListPart(index);
    }
    this.startingMomentIndex = index;
  }

  public fetchSpecialSectionImageData = async () => {
    if (this.specialSectionImageLoading || !StorageServiceInstance.selectedGameData) return;

    this.specialSectionImageInfo = [];
    this.specialSectionPublishedPhotosCount = 0;
    this.specialSecitonImageCompressRate = 1.0;
    this.placePickerSectionFrame = null;
    this.specialSectionImageLoading = true;

    try {
      const request: GetGameMomentsRequests = this.generateMomentRequest(false, true);
      const ms = request.mappedSeat;
      if (ms?.width && ms?.height) {
        this.placePickerSectionFrame = {
          sectionWidth: ms.width,
          sectionHeight: ms.height,
        };
      }

      const { data, error } = await fetchGameMomentsRequest(request);

      if (error) {
        this.specialSectionImageInfo = [];
        this.specialSectionPublishedPhotosCount = 0;
        this.specialSecitonImageCompressRate = 1.0;
        this.placePickerSectionFrame = null;
        AntdMessage.warning(error);
      } else if (!data || !Array.isArray(data.moments)) {
        this.specialSectionImageInfo = [];
        this.specialSectionPublishedPhotosCount = 0;
        this.specialSecitonImageCompressRate = 1.0;
        this.placePickerSectionFrame = null;
        const fallback = (data as { error?: string; message?: string } | null)?.error
          || (data as { error?: string; message?: string } | null)?.message;
        AntdMessage.warning(
          typeof fallback === "string" && fallback.trim() ? fallback.trim() : "Could not load moments for this seat.",
        );
      } else {
        const sortedMoments = data.moments.sort(this.compareMoments);
        this.specialSectionImageInfo = sortedMoments;
        this.specialSectionPublishedPhotosCount = data.momentosTotalCount
          ?? sortedMoments.reduce(
            (acc, m) => acc + (m.momentoLinks?.length || 0),
            0,
          );
        this.specialSecitonImageCompressRate = request.resizeWidth ? request.compressRate : undefined;
        if (this.placePickerSectionFrame && data.width && data.height) {
          this.placePickerSectionFrame = {
            ...this.placePickerSectionFrame,
            bitmapWidth: data.width,
            bitmapHeight: data.height,
          };
        }
      }
    } catch (err) {
      showErrorNotification(`[fetchMomentList] ${err.message}`);
    } finally {
      this.specialSectionImageLoading = false;
    }
  };

  // experimental
  public payForTheGame = async (email, gameId) => {
    const result = await payForTheGame(email, gameId);
    // reload moments
    this.fetchMomentList();
  }

  public addPendingPayment = async (email, gameId) => {
    const result = await addPendingPayment(email, gameId);
  }

  // update download paid status
  @action private setDownloadPaidStatus = (paid: boolean) => {
    this.isDownloadPaid = paid;
  }

  // get download paid status from the database
  @action public getDownloadPaidStatus = async (
    email: string,
    gameId: string,
    section: string,
    row: string,
    seat: string,
  ) => {
    const isDownloadPaidStatus = await getDownloadPaidStatus(
      email,
      gameId,
      section,
      row,
      seat,
    );

    // update status
    this.setDownloadPaidStatus(isDownloadPaidStatus);
    return isDownloadPaidStatus;
  }

  // move to the previous session
  @action public moveToThePreviousSession = async (previousSession: UniqueUser) => {
    if (!previousSession) {
      return;
    }

    StorageServiceInstance.savePreviousSessionToStorage(previousSession);
    await updateLastSessionDate(previousSession._id);

    this.setMomentIdToScroll(null);
    this.setStartingMomentIndex(0);
    AppServiceInstance.history.push(Routes.MY_MOMENTS);
  }

  // add a purchase link and return its id
  public addPurchaseLink = async (
    momentId: string,
    momentoIndex: number,
    imageLink: string,
    width?: number,
    height?: number,
  ) => {
    const moment = this.momentList.find(
      (m) => m._id === momentId,
    );

    if (!moment || momentoIndex < 0 || moment.momentoLinks.length <= momentoIndex || !imageLink) {
      return "";
    }

    // const url = moment.momentoLinks[momentoIndex];

    const EMPTY_EMAIL = "no-reply@momento.com";
    const response = await addPurchaseLink({
      url: imageLink,
      email: StorageServiceInstance.selectedEmail || EMPTY_EMAIL,
      width: width || this.returnedMomentWidth,
      height: height || this.returnedMomentHeight,
      isSpecialSection: StorageServiceInstance.isGaMomentContext(),
      section: StorageServiceInstance.isGaMomentContext()
        ? StorageServiceInstance.selectedSpecialSection : StorageServiceInstance.selectedSection,
      row: StorageServiceInstance.isGaMomentContext()
        ? "" : StorageServiceInstance.selectedRow,
      seat: StorageServiceInstance.isGaMomentContext()
        ? "" : StorageServiceInstance.selectedSeat,
      gameId: StorageServiceInstance.selectedGame,
      momentId,
      momentoIndex,
      caption: moment.text || "Momento",
    });

    return response;
  }

  // add a new print order and return its number
  public reservePrintOrderNumber = async (
  ) => {
    const response = await reservePrintOrderNumber(StorageServiceInstance.selectedGame);

    return { number: response, gameId: StorageServiceInstance.selectedGame };
  }

  // emulate get image request
  public testGetPurchasedImage = async (imageId: string, ticketId: string) => {
    const imageInfo = await getPurchasedImage(imageId, ticketId, StorageServiceInstance.selectedEmail);
    return imageInfo;
  }

  // performs necessary saving and moves router to the next page
  @action public moveToTheNextPage = async (id: string) => {
    const nextPageId = StorageServiceInstance.getNextPage(id);
    // additional preparations and current user save
    if (nextPageId === Routes.MY_MOMENTS) {
      try {
        await StorageServiceInstance.saveUserDetailsToDatabase();
      } catch (err) {
        AntdMessage.error(err?.message || "Unable to save. Please check your details and try again.");
        return;
      }
      this.fetchMomentList();
      this.setMomentIdToScroll(null);
      this.setStartingMomentIndex(0);
    }

    StorageServiceInstance.moveToTheNextPage(id);
  }

  // moves router to the previous page
  @action public moveToThePreviousPage = (id: string) => {
    StorageServiceInstance.moveToThePreviousPage(id);
  }

  /** Wipes all moment state for sign-out. Call after StorageService.resetAllStateForSignOut. */
  @action public resetAllStateForSignOut = (): void => {
    this.momentList = [];
    this.momentsPreparedUntil = 0;
    this.totalMomentsCount = 0;
    this.totalMomentosCount = 0;
    this.isPaid = false;
    this.isDownloadPaid = false;
    this.momentsData = [];
    this.selectedMoment = null;
    this.loading = false;
    this.lastMomentRequest = null;
    this.selectedMomentoSlide = null;
    this.gifPlaying = false;
    this.gifLoading = false;
    this.gifImageToPlay = null;
    this.momentIdToScroll = null;
    this.startingMomentIndex = 0;
    this.specialSectionImageInfo = [];
    this.specialSectionPublishedPhotosCount = 0;
    this.specialSectionImageLoading = false;
    this.placePickerSectionFrame = null;
    this.cacheService = new MomentImageCacheService();
  }

  /** MobX/UI: offsets affect `getCanvasImagePosition` but are not on Storage — include in canvas deps. */
  public getFanCameraOffsetKey = (): string => [
    this.horizontalOffset ?? "",
    this.verticalOffset ?? "",
  ].join("|");

  /**
   * Camera + zoom defaults from the last moments API response (for restoring after View All).
   */
  public exportViewAllDisplaySnapshot = (): {
    centerCoords: Coords | null;
    defaultZoomWidth: number | null;
    defaultZoomHeight: number | null;
    offsetY: number | null;
    horizontalOffset: number | null;
    verticalOffset: number | null;
    returnedMomentWidth: number;
    returnedMomentHeight: number;
  } => ({
    centerCoords: this.centerCoords,
    defaultZoomWidth: this.defaultZoomWidth,
    defaultZoomHeight: this.defaultZoomHeight,
    offsetY: this.offsetY,
    horizontalOffset: this.horizontalOffset,
    verticalOffset: this.verticalOffset,
    returnedMomentWidth: this.returnedMomentWidth,
    returnedMomentHeight: this.returnedMomentHeight,
  });

  @action public applyViewAllDisplaySnapshot = (snap: {
    centerCoords: Coords | null;
    defaultZoomWidth: number | null;
    defaultZoomHeight: number | null;
    offsetY: number | null;
    horizontalOffset: number | null;
    verticalOffset: number | null;
    returnedMomentWidth: number;
    returnedMomentHeight: number;
  }): void => {
    this.centerCoords = snap.centerCoords;
    this.defaultZoomWidth = snap.defaultZoomWidth;
    this.defaultZoomHeight = snap.defaultZoomHeight;
    this.offsetY = snap.offsetY;
    this.horizontalOffset = snap.horizontalOffset;
    this.verticalOffset = snap.verticalOffset;
    this.returnedMomentWidth = snap.returnedMomentWidth;
    this.returnedMomentHeight = snap.returnedMomentHeight;
  };

  public getImagePosition(momentId: string, momentoIndex: number) {
    const savedCropData = StorageServiceInstance.getImageCropData(
      momentId || this.selectedMoment._id,
      momentoIndex,
    );

    if (savedCropData) {
      return {
        x: savedCropData.x,
        y: savedCropData.y,
        width: savedCropData.width,
        height: savedCropData.height,
        filter: savedCropData.filter,
      };
    }

    return {
      x: StorageServiceInstance.centerCoords.x - StorageServiceInstance.defaultZoomWidth / 2,
      y: StorageServiceInstance.centerCoords.y - StorageServiceInstance.defaultZoomHeight / 2 - StorageServiceInstance.offsetY,
      width: StorageServiceInstance.defaultZoomWidth,
      height: StorageServiceInstance.defaultZoomHeight,
    };
  }

  public getAllMomentImagesPositions(momentId: string, len: number) {
    const positions = [];
    for (let ind = 0; ind < len; ind += 1) {
      positions.push(this.getImagePosition(momentId, ind));
    }

    return positions;
  }
}

export const MomentServiceInstance = new MomentService();

export const MomentServiceContext = createContext(MomentServiceInstance);
